// ============================================================================
// EventDetailPage — per-event detail page (replaces homepage WorkshopCard).
// ============================================================================
// Visual structure mirrors the prior WorkshopCard so the rich event detail
// (date/time grid, modal buttons for agenda/about/presenters, full body
// rendering with paragraphs + bullets, What-to-bring callout, bottom CTA
// bar with Register button) is preserved exactly — just for one event at
// a time with surrounding page chrome.
//
// Routed via #event/<event_id>. Role-aware via useAuth: anonymous viewers
// see public events only; AMP sees public+amp; AGC sees public+agc; UIF
// sees all four. The /api/events handler does the visibility filtering;
// this page just looks up the event in the returned set.
//
// The Agenda/About/Presenters modals (AgendaModal, DescriptionModal,
// PresentersModal) currently use hardcoded Food Safety content from
// src/content/workshop.ts. When non-Food-Safety events come online,
// those modals will need to become event-aware. For now the buttons work
// and show Food Safety content regardless of the event — flagged as
// tech debt for Phase 5 (future event seeds).
// ============================================================================

import { useEffect, useState } from 'react';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  DollarSign,
  Users,
  Utensils,
  UtensilsCrossed,
  Coffee,
  Cookie,
  ListChecks,
  FileText,
  UserCircle2,
  Pencil,
  Trash2,
  QrCode,
  Eye,
  Send,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api, type PublicEvent, type Audience, type Provision, type EventDay } from '../api';
import { useAuth } from '../auth';
import { SignupForm } from './SignupForm';
import { AgendaModal } from './AgendaModal';
import { DescriptionModal } from './DescriptionModal';
import { PresentersModal } from './PresentersModal';
import { MarkdownBody } from './MarkdownBody';
import { QrCodeModal } from './QrCodeModal';

// Phase 3d-1e: provisions render config — order here drives left-to-right
// display order in the badge row.
const PROVISION_DISPLAY: { value: Provision; label: string; Icon: LucideIcon }[] = [
  { value: 'breakfast', label: 'Breakfast provided', Icon: Coffee },
  { value: 'lunch',     label: 'Lunch provided',     Icon: Utensils },
  { value: 'dinner',    label: 'Dinner provided',    Icon: UtensilsCrossed },
  { value: 'snacks',    label: 'Snacks & coffee',    Icon: Cookie },
];

interface EventDetailPageProps {
  // Either eventId (legacy /#event/<id>) or slug (/#e/<slug>) — pick one.
  eventId?: number;
  slug?: string;
}

const AUDIENCE_LABEL: Record<Audience, string> = {
  public:   'Open to public',
  amp:      'AMP Producers only',
  agc:      'AGC only',
  internal: 'Internal · UIF',
};

const AUDIENCE_BADGE: Record<Audience, string> = {
  public:   'bg-uif-green/10 text-uif-green-dark',
  amp:      'bg-purple-100 text-purple-900',
  agc:      'bg-orange-100 text-orange-900',
  internal: 'bg-gray-100 text-gray-700',
};

// Phase 3d-1c-prep: description rendering moved to MarkdownBody (react-markdown
// + remark-gfm + rehype-raw). The prior hand-rolled paragraph/bullet parser
// only handled the bespoke Food Safety formatting (exact-string italic match
// and bold-before-em-dash bullet lead) and silently dropped real markdown
// tokens like **bold**, *italic*, and links. Authoring is now standard markdown.

// Phase 026: PrerequisitesBlock — fetches required prior events by id and
// renders a small section with linked event titles + the equivalent-experience
// free-text note. Self-contained so its event fetch doesn't bloat the parent.
function PrerequisitesBlock({ prereqIds, prereqText }: { prereqIds: number[]; prereqText: string }) {
  const [linked, setLinked] = useState<PublicEvent[]>([]);

  useEffect(() => {
    if (prereqIds.length === 0) return;
    api.events({ includePast: true })
      .then((d) => {
        const ids = new Set(prereqIds);
        setLinked((d.events ?? []).filter((e) => ids.has(e.event_id)));
      })
      .catch(() => {});
  }, [prereqIds.join(',')]);

  return (
    <div className="px-6 pb-6 -mt-2">
      <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-sm text-gray-700 space-y-2">
        <div className="font-semibold text-amber-900 uppercase tracking-wider text-xs">Prerequisites</div>
        {linked.length > 0 && (
          <div>
            <div className="text-xs text-gray-600 mb-1">Must have completed:</div>
            <ul className="list-disc list-outside pl-5 space-y-1">
              {linked.map((e) => (
                <li key={e.event_id}>
                  <a href={`#event/${e.event_id}`} className="text-uif-green hover:underline">
                    {e.title}
                  </a>
                  <span className="text-gray-500"> · {new Date(e.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {prereqText && (
          <div>
            {linked.length > 0 && <div className="text-xs text-gray-600 mb-1">— or equivalent —</div>}
            <div className="italic">{prereqText}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// Phase 3d-1e: render provisions as a centered row of icon+label badges
// separated by bullets. Renders nothing when the event has no provisions set.
function renderProvisions(provisions: Provision[] | null | undefined) {
  if (!provisions || provisions.length === 0) return null;
  const set = new Set(provisions);
  const selected = PROVISION_DISPLAY.filter((p) => set.has(p.value));
  if (selected.length === 0) return null;
  return (
    <div className="flex items-center justify-center flex-wrap gap-x-3 gap-y-2 my-6 text-uif-green-dark font-bold uppercase tracking-wider text-sm">
      {selected.map((p, idx) => (
        <span key={p.value} className="inline-flex items-center">
          {idx > 0 && (
            <span aria-hidden="true" className="mx-3 text-uif-green/40">•</span>
          )}
          <span className="inline-flex items-center gap-2">
            <p.Icon className="h-4 w-4" />
            {p.label}
          </span>
        </span>
      ))}
    </div>
  );
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function fmtTime(d: Date): string {
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

// Phase 028: renderDayCards — show each day's date + times using event.days[].
// Each day inherits times from the event-level start/end if not overridden.
function renderDayCards(days: EventDay[], fallbackStartISO: string, fallbackEndISO: string): React.ReactNode {
  const fStart = new Date(fallbackStartISO);
  const fEnd = new Date(fallbackEndISO);
  const fStartTime = `${String(fStart.getUTCHours()).padStart(2, '0')}:${String(fStart.getUTCMinutes()).padStart(2, '0')}`;
  const fEndTime = `${String(fEnd.getUTCHours()).padStart(2, '0')}:${String(fEnd.getUTCMinutes()).padStart(2, '0')}`;
  return (
    <>
      {days.map((d, i) => {
        const dateObj = new Date(d.day_date + 'T00:00:00Z');
        const dateStr = fmtDate(dateObj);
        const start = (d.start_time ?? fStartTime).slice(0, 5);
        const end = (d.end_time ?? fEndTime).slice(0, 5);
        // Format HH:MM → 12-hour with am/pm.
        const fmtT = (hm: string) => {
          const [h, m] = hm.split(':').map((s) => Number(s));
          const ampm = h >= 12 ? 'PM' : 'AM';
          const h12 = h % 12 === 0 ? 12 : h % 12;
          return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
        };
        return (
          <div key={i} className={i > 0 ? 'mt-1' : ''}>
            <div className="font-semibold text-gray-900">{dateStr}</div>
            <div className="text-gray-600">{fmtT(start)} – {fmtT(end)}</div>
          </div>
        );
      })}
    </>
  );
}

// renderSchedule — single-day events get a date + time pair (existing layout).
// Multi-day events get one row per day, each with the assumed daily window
// (uses start_date.time as the daily opening, end_date.time as the daily close).
// This better communicates "two-day workshop, 8:30am–5:00pm each day" rather
// than the confusing contiguous-range "Mon 8:30am – Tue 5:00pm".
function renderSchedule(startISO: string, endISO: string): React.ReactNode {
  const start = new Date(startISO);
  const end = new Date(endISO);
  const sameDay =
    start.getUTCFullYear() === end.getUTCFullYear() &&
    start.getUTCMonth() === end.getUTCMonth() &&
    start.getUTCDate() === end.getUTCDate();

  if (sameDay) {
    return (
      <>
        <div className="font-semibold text-gray-900">{fmtDate(start)}</div>
        <div className="text-gray-600">{fmtTime(start)} – {fmtTime(end)}</div>
      </>
    );
  }

  // Multi-day: enumerate each calendar day from start to end (inclusive).
  // Daily window = start.time (opening) → end.time (closing). Same window
  // each day until we add per-day schedules in a later phase.
  const dayCount =
    Math.floor((Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()) -
                Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate())) /
               (24 * 60 * 60 * 1000)) + 1;

  const days: React.ReactNode[] = [];
  for (let i = 0; i < dayCount; i++) {
    const dayDate = new Date(Date.UTC(
      start.getUTCFullYear(),
      start.getUTCMonth(),
      start.getUTCDate() + i,
    ));
    days.push(
      <div key={i} className={i > 0 ? 'mt-1' : ''}>
        <div className="font-semibold text-gray-900">{fmtDate(dayDate)}</div>
        <div className="text-gray-600">{fmtTime(start)} – {fmtTime(end)}</div>
      </div>
    );
  }
  return <>{days}</>;
}

export function EventDetailPage({ eventId, slug }: EventDetailPageProps) {
  const { token, user } = useAuth();
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showRegister, setShowRegister] = useState(false);
  const [showAgenda, setShowAgenda] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [showPresenters, setShowPresenters] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const isUif = user?.role === 'uif';

  const handleEdit = () => {
    // Edit route is keyed by calendar_entry_id (which the backend PATCH
    // endpoint expects), not event_id. For older events the two often
    // match by coincidence, but materialized instances make them diverge.
    if (event) window.location.hash = `#admin-event-edit/${event.calendar_entry_id}`;
  };

  const handleArchive = async () => {
    if (!token) return;
    const yes = window.confirm(
      'Archive this event? Registrations will be preserved but the event will no longer appear on the calendar. This can be undone via dbForge if needed.',
    );
    if (!yes) return;
    setArchiving(true);
    try {
      if (!event) return;
      await api.archiveEvent(token, event.calendar_entry_id);
      window.location.hash = '';
    } catch (err: any) {
      alert(err?.message || 'Could not archive event.');
      setArchiving(false);
    }
  };

  // Round 4: flip is_draft from true → false. UIF only.
  const handleGoLive = async () => {
    if (!token || !event) return;
    const yes = window.confirm(
      `Publish "${event.title}"? It will be visible on the public calendar immediately. ` +
      `You can still archive or edit it after publishing.`,
    );
    if (!yes) return;
    try {
      await api.updateEvent(token, event.calendar_entry_id, { is_draft: false });
      // Re-fetch to refresh badge state
      setEvent({ ...event, is_draft: false });
    } catch (err: any) {
      alert(err?.message || 'Could not publish event.');
    }
  };

  // Optional inverse: take a live event back to draft (e.g., to fix a typo).
  const handleUnpublish = async () => {
    if (!token || !event) return;
    const yes = window.confirm(
      `Move "${event.title}" back to draft? It will be removed from the public calendar.`,
    );
    if (!yes) return;
    try {
      await api.updateEvent(token, event.calendar_entry_id, { is_draft: true });
      setEvent({ ...event, is_draft: true });
    } catch (err: any) {
      alert(err?.message || 'Could not unpublish event.');
    }
  };

  useEffect(() => {
    setLoading(true);
    setError('');
    // includeTemplates + includeDrafts so UIF admins can preview drafts and
    // edit templates from the public detail page URL. Non-UIF callers ignore
    // these flags (server-side check); they still only see live, non-template
    // events.
    api
      .events({ includePast: true, includeTemplates: true, includeDrafts: true, token: token ?? undefined })
      .then((data) => {
        const list = data.events ?? [];
        const found = slug
          ? list.find((e) => e.slug === slug)
          : list.find((e) => e.event_id === eventId);
        if (!found) setError('Event not found or not visible to your role.');
        else setEvent(found);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load event.');
        setLoading(false);
      });
  }, [eventId, slug, token]);

  const handleBack = () => {
    window.location.hash = '';
  };

  const audience: Audience = (event?.audience as Audience) ?? 'public';
  const availability = event?.availability_status ?? 'open';
  const isFull = availability === 'closed';
  const isWaitlist = availability === 'waitlist';
  // Past events: end_date < now. Once an event is past, no more registrations
  // regardless of nominal seat availability. UI updates copy + disables button.
  const isPast = event ? new Date(event.end_date) < new Date() : false;
  const seatsRemaining = event
    ? Math.max((event.max_attendees ?? 0) - (event.current_count ?? 0), 0)
    : 0;

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col">
      <header className="border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" aria-label="Up in Farms — home">
            <img src="/logo.jpg" alt="Up in Farms Food Hub" className="h-14 md:h-16 w-auto" />
            <span className="sr-only">Up in Farms</span>
          </a>
          <a
            href="mailto:support@upinfarms.com"
            className="hidden sm:inline text-sm text-gray-600 hover:text-uif-green"
          >
            support@upinfarms.com
          </a>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-uif-green-dark"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to calendar
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            {event && (
              <button
                type="button"
                onClick={() => setShowQr(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                title="QR code for this event"
              >
                <QrCode className="h-4 w-4" />
                QR code
              </button>
            )}
            {isUif && event && (
              <>
                {event.is_draft && (
                  <button
                    type="button"
                    onClick={handleGoLive}
                    disabled={archiving}
                    className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-sm hover:bg-uif-green-dark"
                    title="Publish this event to the public calendar"
                  >
                    <Send className="h-4 w-4" />
                    Go Live
                  </button>
                )}
                {!event.is_draft && (
                  <button
                    type="button"
                    onClick={handleUnpublish}
                    disabled={archiving}
                    className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm text-amber-800 hover:bg-amber-50"
                    title="Move this event back to draft"
                  >
                    <Eye className="h-4 w-4" />
                    Unpublish
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleEdit}
                  disabled={archiving}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </button>
                <a
                  href={`#admin/budgets/${event.calendar_entry_id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                  title="Open the per-event budget"
                >
                  <DollarSign className="h-4 w-4" />
                  Budget
                </a>
                <button
                  type="button"
                  onClick={handleArchive}
                  disabled={archiving}
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  {archiving ? 'Archiving…' : 'Archive'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Round 4: Draft mode banner for UIF previewers. Anonymous + non-UIF
            users can't load drafts at all (server filters them), so this banner
            only ever shows to authenticated UIF admins. */}
        {event?.is_draft && isUif && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-4 flex items-start gap-3">
            <Eye className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-amber-900">
                Preview mode — this event is NOT visible to the public
              </div>
              <p className="text-xs text-amber-800 mt-0.5">
                Click <strong>Go Live</strong> in the top bar when you're ready to publish to the
                public calendar. You can still edit or archive after publishing.
              </p>
            </div>
          </div>
        )}

        {loading && <p className="text-center text-gray-500 py-12">Loading event…</p>}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-center">
            {error}
          </div>
        )}

        {event && !loading && !error && (
          <article className="rounded-lg border border-gray-200 shadow-sm overflow-hidden bg-white">
            {/* Optional hero photo — backend field hero_photo_url. When the
                backend exposes it, this renders automatically. Until then,
                events with no photo skip this section. */}
            {event.hero_photo_url && (
              <div className="w-full aspect-[21/9] bg-gray-100 overflow-hidden">
                <img
                  src={event.hero_photo_url}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <header className="bg-uif-green/5 border-b border-gray-200 px-6 py-5">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className={'inline-block px-2.5 py-0.5 rounded text-xs font-medium ' + AUDIENCE_BADGE[audience]}>
                  {AUDIENCE_LABEL[audience]}
                </span>
                {/* Phase 027 transitional: hide the series badge for series_type='multi_day'
                    while we wait for Phase A to introduce a real multi-day event model.
                    Multi-day events are properly "one event with multiple day rows" — they
                    don't belong in a series. The current badge says "Day 1 of 2" which is
                    confusing when shown alongside a single 2-day date range. */}
                {event.series_label && event.series_type !== 'multi_day' && (
                  <span className="inline-block px-2.5 py-0.5 rounded text-xs font-medium bg-uif-brown/15 text-uif-brown-dark">
                    {event.series_label}
                    {event.instance_label && ` · ${event.instance_label}`}
                  </span>
                )}
              </div>
              <h2 className="text-2xl font-bold text-uif-green-dark mb-1">{event.title}</h2>
              <p className="text-uif-green italic">Free to attend. Priceless for your farm.</p>
            </header>

            <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div className="flex items-start gap-2">
                <Calendar className="h-5 w-5 text-uif-green flex-shrink-0 mt-0.5" />
                <div>
                  {/* Phase 028: render per-day schedule from event.days when present;
                      fall back to the old start/end-derived schedule otherwise. */}
                  {event.days && event.days.length > 0
                    ? renderDayCards(event.days, event.start_date, event.end_date)
                    : renderSchedule(event.start_date, event.end_date)}
                </div>
              </div>

              {event.doors_open_text && (
                <div className="flex items-start gap-2">
                  <Clock className="h-5 w-5 text-uif-green flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-900">Doors open</div>
                    <div className="text-gray-600">{event.doors_open_text}</div>
                  </div>
                </div>
              )}

              {/* Task #15: hide physical location for pure online events. */}
              {event.format !== 'online' && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-5 w-5 text-uif-green flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-900">Location</div>
                    <div className="text-gray-600">{event.location ?? 'TBD'}</div>
                    {event.format === 'hybrid' && (
                      <div className="text-xs text-uif-green-dark mt-0.5">
                        🎥 Also offered online — choose your mode when registering
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Online block for online/hybrid events. */}
              {(event.format === 'online' || event.format === 'hybrid') && (
                <div className="flex items-start gap-2">
                  <span className="h-5 w-5 text-uif-green flex-shrink-0 mt-0.5 text-base leading-none">🎥</span>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">
                      {event.format === 'online' ? 'Online event' : 'Online option'}
                    </div>
                    {event.online_provider && (
                      <div className="text-xs text-gray-500 uppercase tracking-wider">
                        via {({ zoom: 'Zoom', teams: 'Microsoft Teams', meet: 'Google Meet', webex: 'Webex', other: 'Online meeting' } as Record<string, string>)[event.online_provider] || 'Online meeting'}
                      </div>
                    )}
                    {/* Meeting URL display + CTA varies by visibility */}
                    {event.online_meeting_url ? (
                      // url_visibility=public → URL is in the response, show button
                      <a
                        href={event.online_meeting_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 mt-2 rounded-md bg-uif-green text-white text-sm font-semibold px-3 py-1.5 hover:bg-uif-green-dark"
                      >
                        Join meeting →
                      </a>
                    ) : event.online_url_visibility === 'farmer_signed_in' ? (
                      <div className="text-sm text-gray-600 mt-1">
                        <a href="#farmer-signin" className="text-uif-green-dark hover:underline font-medium">Sign in to your producer account</a> to access the meeting link.
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600 mt-1">
                        The meeting link is sent in your registration confirmation email.
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2">
                <DollarSign className="h-5 w-5 text-uif-green flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-gray-900">Cost</div>
                  <div className="text-gray-600">{event.cost_text || 'Free'}</div>
                </div>
              </div>

              {event.stipend_text && (
                <div className="flex items-start gap-2 md:col-span-2">
                  <DollarSign className="h-5 w-5 text-uif-brown flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-900">Stipend</div>
                    <div className="text-gray-600">{event.stipend_text}</div>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2 md:col-span-2">
                <Users className="h-5 w-5 text-uif-green flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-gray-900">
                    {isPast
                      ? 'This event is now closed'
                      : isFull
                        ? 'Event full'
                        : isWaitlist
                          ? 'Event full — waitlist open'
                          : `${seatsRemaining} of ${event.max_attendees ?? 16} seats available`}
                  </div>
                  <div className="text-gray-600">
                    {isPast
                      ? 'Registration is closed for past events.'
                      : `Limited to ${event.max_attendees ?? 16} farms for a hands-on, small-group format.`}
                  </div>
                </div>
              </div>

              {/* Registration deadline display. Shown only when the admin set
                  one and we're not past the event. Color shifts to amber when
                  the deadline is within 48 hours so it reads as a nudge. */}
              {!isPast && event.registration_deadline && (() => {
                const deadlineMs = Date.parse(event.registration_deadline);
                const hoursLeft = (deadlineMs - Date.now()) / (1000 * 60 * 60);
                const past = hoursLeft < 0;
                const soon = hoursLeft >= 0 && hoursLeft <= 48;
                const tone = past
                  ? 'text-red-700'
                  : soon
                    ? 'text-amber-700'
                    : 'text-gray-600';
                const label = past
                  ? 'Registration closed'
                  : 'Registration closes';
                const deadlineStr = new Date(event.registration_deadline).toLocaleString('en-US', {
                  weekday: 'short', month: 'short', day: 'numeric',
                  hour: 'numeric', minute: '2-digit',
                  timeZoneName: 'short',
                });
                return (
                  <div className="flex items-start gap-2 md:col-span-2">
                    <Clock className={'h-5 w-5 flex-shrink-0 mt-0.5 ' + (past ? 'text-red-600' : soon ? 'text-amber-600' : 'text-uif-green')} />
                    <div>
                      <div className="font-semibold text-gray-900">{label}</div>
                      <div className={tone}>
                        {deadlineStr}
                        {soon && !past && (
                          <span className="ml-2 text-xs italic">
                            ({hoursLeft < 1 ? 'less than an hour' : `~${Math.round(hoursLeft)} hours`} left)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Modal trigger buttons. Each only renders when the event has
                markdown content for that modal — events without an agenda
                don't get a dead "View agenda" button.  The grid columns auto-
                adjust to however many buttons are present (1, 2, or 3). */}
            {(event.agenda_md || event.about_md || event.presenters_md) && (() => {
              const buttons: React.ReactNode[] = [];
              if (event.agenda_md) {
                buttons.push(
                  <button
                    key="agenda"
                    type="button"
                    onClick={() => setShowAgenda(true)}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-uif-green/30 bg-white px-3 py-2 text-sm font-medium text-uif-green-dark hover:bg-uif-green/5 transition-colors"
                  >
                    <ListChecks className="h-4 w-4" />
                    View agenda
                  </button>,
                );
              }
              if (event.about_md) {
                buttons.push(
                  <button
                    key="about"
                    type="button"
                    onClick={() => setShowDescription(true)}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-uif-green/30 bg-white px-3 py-2 text-sm font-medium text-uif-green-dark hover:bg-uif-green/5 transition-colors"
                  >
                    <FileText className="h-4 w-4" />
                    About the workshop
                  </button>,
                );
              }
              if (event.presenters_md) {
                buttons.push(
                  <button
                    key="presenters"
                    type="button"
                    onClick={() => setShowPresenters(true)}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-uif-green/30 bg-white px-3 py-2 text-sm font-medium text-uif-green-dark hover:bg-uif-green/5 transition-colors"
                  >
                    <UserCircle2 className="h-4 w-4" />
                    Meet the presenters
                  </button>,
                );
              }
              const cols = ['', 'sm:grid-cols-1', 'sm:grid-cols-2', 'sm:grid-cols-3'][buttons.length] ?? '';
              return (
                <div className={`px-6 pt-4 pb-2 border-t border-gray-100 grid grid-cols-1 ${cols} gap-2`}>
                  {buttons}
                </div>
              );
            })()}

            {/* Round 3: Per-instance focus note. Only shows for materialized
                instances of a recurring template (parent_template_id set) when
                the admin filled in instance_notes — e.g. "Week 3: covering
                cover-crop selection." Differentiates one instance from another
                without cluttering the shared template content. */}
            {event.parent_template_id != null && event.instance_notes && (
              <div className="px-6 pt-4">
                <div className="bg-uif-green/5 border-l-4 border-uif-green rounded-r-md px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-uif-green-dark mb-1">
                    This week's focus
                  </div>
                  <div className="prose-content text-sm">
                    <MarkdownBody source={event.instance_notes} />
                  </div>
                </div>
              </div>
            )}

            {(event.description || (event.provisions && event.provisions.length > 0)) && (
              <div className="px-6 pb-6 pt-4">
                <div className="prose-content">
                  {event.description && <MarkdownBody source={event.description} />}
                  {renderProvisions(event.provisions)}
                </div>
              </div>
            )}

            {event.materials && (
              <div className="px-6 pb-6 -mt-2">
                <div className="bg-uif-brown/5 border border-uif-brown/20 rounded-md px-4 py-3 text-sm text-gray-700">
                  <span className="font-semibold text-uif-brown-dark">What to bring:</span>{' '}
                  {event.materials}
                </div>
              </div>
            )}

            {/* Phase 026: Prerequisites */}
            {((event.prereq_event_ids && event.prereq_event_ids.length > 0) || event.prereq_text) && (
              <PrerequisitesBlock
                prereqIds={event.prereq_event_ids ?? []}
                prereqText={event.prereq_text ?? ''}
              />
            )}

            {/* Optional video embed — typically a YouTube ad campaign for the
                event. Renders as a 16:9 iframe inside the card. Backend field
                video_embed_url. Skipped when not set. */}
            {event.video_embed_url && (
              <div className="px-6 pb-6">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3">
                  Watch a preview
                </h3>
                <div className="aspect-video w-full bg-gray-100 rounded-md overflow-hidden">
                  <iframe
                    src={event.video_embed_url}
                    title={`${event.title} — video preview`}
                    className="w-full h-full"
                    frameBorder={0}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            <div className="bg-gray-50 border-t border-gray-200 px-6 py-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <div className="text-sm text-gray-600">
                {isPast
                  ? 'This event has ended. Browse the calendar for upcoming events.'
                  : isFull
                    ? 'Sorry, registration is closed for this event.'
                    : isWaitlist
                      ? 'Event is full. Join the waitlist and we’ll reach out if a seat opens.'
                      : 'Reserve your seat — we’ll confirm by email and follow up with day-of details.'}
              </div>
              <button type="button" onClick={() => setShowRegister(true)} disabled={isPast || isFull}
                className="ininline-flex items-center justify-center rounded-md bg-uif-green text-white font-semibold px-6 py-2.5 hover:bg-uif-green-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
                {isPast ? 'Event ended' : isFull ? 'Registration closed' : isWaitlist ? 'Join waitlist' : 'Register'}
              </button>
            </div>
          </article>
        )}
      </main>

      <footer className="border-t border-gray-200 mt-12">
        <div className="max-w-5xl mx-auto px-4 py-8 text-center text-sm text-gray-500 space-y-2">
          <p>
            Up in Farms LLC · 2093 Lakeland Dr, Jackson, MS 39216 ·{' '}
            <a href="mailto:support@upinfarms.com" className="text-uif-green hover:underline">
              support@upinfarms.com
            </a>
          </p>
        </div>
      </footer>

      {event && (
        <SignupForm event={event} open={showRegister} onClose={() => setShowRegister(false)} />
      )}
      {event?.agenda_md && (
        <AgendaModal
          open={showAgenda}
          onClose={() => setShowAgenda(false)}
          content={event.agenda_md}
          days={event.days}
          eventTitle={event.title}
        />
      )}
      {event?.about_md && (
        <DescriptionModal open={showDescription} onClose={() => setShowDescription(false)} content={event.about_md} />
      )}
      {event?.presenters_md && (
        <PresentersModal open={showPresenters} onClose={() => setShowPresenters(false)} content={event.presenters_md} />
      )}
      {event && (
        <QrCodeModal
          open={showQr}
          onClose={() => setShowQr(false)}
          url={event.slug
            ? `${window.location.origin}/#e/${event.slug}`
            : `${window.location.origin}/#event/${event.event_id}`}
          title={`QR code · ${event.title}`}
          filename={`uif-event-${event.slug || event.event_id}`}
        />
      )}
    </div>
  );
}
